import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Button,
  Box,
  TextField,
  Autocomplete,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Avatar,
  SvgIcon,
  Stack,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  OutlinedInput,
  ListItemText
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import { ISubjectSelectOption } from '../../../types/subjectTypes';
import { IStoryPrivilege, IsubjectClaimsMessages, ISubjectStoryClaim, ISelectedSubject, ISubjectPublicationClaim, IPublicationPrivilege, ISubjectPublicationClaimsList, IStoriesFolder } from '../../../types';
import { styles } from './StoryAccessGranularDialog.styles';
import { User, UserGroup } from '@opengov/bnp-core';
import { STORIES_EDITOR_PRIVILEGE, STORIES_PDF_GENERATION_PRIVILEGE, STORIES_VIEWER_PRIVILEGE, STORIES_PUBLISHER_PRIVILEGE } from '@opengov/stories-shared/constants';

interface IAccessDialogProps {
  isBulk: boolean;
  storyTitles?: string[];
  inputRef: React.RefObject<HTMLInputElement>;
  inputName: string;
  messages: IsubjectClaimsMessages;
  sortedOptionsList: ISubjectSelectOption[];
  cols: {
    width: string | number;
    title: string;
    alignment: 'left' | 'center';
  }[];
  subjectsToRender: (ISubjectStoryClaim | ISelectedSubject | ISubjectPublicationClaim)[];
  usersMap: Record<string, User>;
  groupsMap: Record<string, UserGroup>;
  onClose(): void;
  onSave(): void;
  onOptionChange(_: any, value: ISubjectSelectOption[]): void;
  onPrivilegeChange?(e: React.ChangeEvent<HTMLInputElement>, subjectId: string): void;
  onPublicationPrivilegeChange?(e: SelectChangeEvent, subjectId: string): void;
  onSectionIdsChange?(e: SelectChangeEvent<string[]>, subjectId: string): void;
  onRemoveClick(subjectId: string): void;
  isPublicationGranularAccess?: boolean;
  subjectPublicationClaimsList?: ISubjectPublicationClaimsList;
  folderStructure?: IStoriesFolder[];
}

const getAdminColumn = (messages) => {
  return <Stack direction="row" spacing={2} alignItems="center">
    <Avatar>
      <GroupsIcon />
    </Avatar>
    <Typography variant="h4">{messages.admins}</Typography>
  </Stack>;
}

const roles = (messages: IMessages): IRole[] => {
  return [
    {
      value: messages.manageSharedAndViewAll,
      label: messages.reviewer,
      description: messages.reviewerDescription
    },
    {
      value: messages.manageShared,
      label: messages.contributor,
      description: messages.contributorDescription
    },
    {
      value: messages.manageSharedAndCreateNew,
      label: messages.author,
      description: messages.authorDescription
    },
    {
      value: messages.manageAll,
      label: messages.editor,
      description: messages.editorDescription
    }
  ];
}
interface IMessages {
  admins: string;
  manageShared: string;
  contributor: string;
  contributorDescription: string;
  manageSharedAndViewAll: string;
  reviewer: string;
  reviewerDescription: string;
  manageSharedAndCreateNew: string;
  author: string;
  authorDescription: string;
  manageAll: string;
  editor: string;
  editorDescription: string;
}

interface IRole {
  value: string;
  label: string;
  description: string;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 6.5 + ITEM_PADDING_TOP,
      width: 250
    },
  },
};

const findSectionById = (folders: IStoriesFolder[], id: string): IStoriesFolder | undefined => {
  for (const folder of folders) {
    if (folder.id === id) return folder;
    if (folder.children) {
      const found = findSectionById(folder.children, id);
      if (found) return found;
    }
  }
  return undefined;
};

export const StoryGranularAccessDialog: React.FC<IAccessDialogProps> = ({
  isBulk,
  storyTitles = [],
  inputRef,
  inputName,
  messages,
  sortedOptionsList,
  cols,
  subjectsToRender,
  usersMap,
  groupsMap,
  isPublicationGranularAccess,
  subjectPublicationClaimsList,
  onClose,
  onSave,
  onOptionChange,
  onPrivilegeChange,
  onPublicationPrivilegeChange,
  onSectionIdsChange,
  onRemoveClick,
  folderStructure
}) => {
  const rolesList = isPublicationGranularAccess ? roles(messages) : [];
  const [searchInput, setSearchInput] = useState('');

  const handleAutocompleteChange = (
    event: React.SyntheticEvent,
    value: ISubjectSelectOption[],
    reason: string
  ) => {
    if (reason === 'selectOption' && value.length > 0) {
      onOptionChange(event, value);
      setSearchInput('');
    }
  };

  const handleInputChange = (
    _: React.SyntheticEvent,
    value: string,
    reason: string
  ) => {
    // MUI fires `reset` when options re-render; updating state then clears visible input.
    if (reason === 'input' || reason === 'clear') {
      setSearchInput(value);
    }
  };

  const renderOptions = (folderStructure: IStoriesFolder[], selectedValues: string[], level = 0): React.ReactNode[] => {
    return folderStructure.flatMap((folder) => [
      <MenuItem key={folder.id} value={folder.id} sx={{ 
        pl: 2 + level * 2, 
        '&.Mui-selected': { 
          backgroundColor: 'transparent',
          color: 'inherit'
        }
      }}>
        <Checkbox 
          checked={selectedValues.includes(folder.id)}
        />
        <ListItemText primary={folder.displayName} />
      </MenuItem>,
      ...(folder.children ? renderOptions(folder.children, selectedValues, level + 1) : [])
    ]);
  };

  return (
    <Dialog 
      onClose={onClose} 
      open={true} 
      fullWidth 
      maxWidth="md" 
      className={
        isBulk
          ? 'BulkStoryGranularAccessDialog'
          : isPublicationGranularAccess
          ? 'PublicationGranularAccessDialog'
          : 'StoryGranularAccessDialog'
      }
    >
      <DialogTitle>
        {messages.manageAccess}
        <IconButton className="MuiDialog__close-button" aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="bodyDefault">{isPublicationGranularAccess ? messages.publicationManageAccessDescription : messages.manageAccessDescription}</Typography>
        {isBulk && storyTitles.length > 0 && (
          <Typography variant="bodyDefault" sx={styles.mt16}>
            Selected story:{' '}
            <Typography component="span" fontWeight="bold">
              {storyTitles.slice(0, 4).join(', ')}
              {storyTitles.length > 4 && (
                <Typography component="span" sx={styles.userGroupMemberCount}>
                  {messages.more({ count: storyTitles.length - 4 })}
                </Typography>
              )}
            </Typography>
          </Typography>
        )}

        <Autocomplete
          sx={styles.autoComplete}
          fullWidth
          multiple
          options={sortedOptionsList}
          value={[]}
          inputValue={searchInput}
          onInputChange={handleInputChange}
          getOptionKey={(option) => option.id}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => (
            <TextField
              {...params}
              name={inputName}
              placeholder={messages.placeholder}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={styles.option} data-test="subjectOption">
              <Box sx={styles.optionDetailsCol}>
                <Typography sx={styles.optionLabel} data-test="subjectOptionLabel">
                  {option.label}
                </Typography>
                {option.subjectType === 'user' ? (
                  <Typography data-test="userEmail" sx={styles.userEmail}>
                    {option.metadata.email}
                  </Typography>
                ) : (
                  <Typography sx={styles.userGroupMemberCount} data-test="subjectGroupMemberCount">
                    {option.metadata.memberCount === 1 ? messages.oneMember : messages.members({ count: option.metadata.memberCount })}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          onChange={handleAutocompleteChange}
        />

        <Typography variant="h4" sx={styles.peopleWithAccessTitle}>
          {messages.peopleWithAccess}
        </Typography>

        <TableContainer>
          <MuiTable aria-label="Access Table" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                {cols.map((col, index) => (
                  <TableCell 
                    key={col.title} 
                    align={col.alignment} 
                    sx={isPublicationGranularAccess ? { 
                      width: index === 0 ? '40%' : 
                            index === 1 ? '50%' : '10%'
                    } : {
                      width: index === 0 ? '50%' : `${50 / (cols.length - 1)}%`
                    }}
                  >
                    <Typography variant="h5">{col.title}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isPublicationGranularAccess ? (
                <TableRow>
                  <TableCell align={cols[0].alignment} sx={{ width: '40%' }}>
                    {getAdminColumn(messages)}
                  </TableCell>
                  <TableCell align={cols[1].alignment} sx={{ width: '50%' }}>
                    <Typography variant="h5" color="text.disabled">{messages.fullAccess}</Typography>
                  </TableCell>
                  <TableCell align={cols[2].alignment} sx={{ width: '10%' }} />
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell align={cols[0].alignment}>
                    {getAdminColumn(messages)}
                  </TableCell>
                  {[STORIES_VIEWER_PRIVILEGE, STORIES_EDITOR_PRIVILEGE, STORIES_PDF_GENERATION_PRIVILEGE, STORIES_PUBLISHER_PRIVILEGE].map((priv) => (
                    <TableCell key={priv} align={cols[1].alignment}>
                      <Checkbox name={priv} checked disabled />
                    </TableCell>
                  ))}
                  <TableCell align={cols[5].alignment} />
                </TableRow>
              )}

              {subjectsToRender.map((subject) => {
                const isUser = subject.subjectType === 'user';
                const isGroup = subject.subjectType === 'group';
                const user = isUser ? usersMap[subject.subjectId] : null;
                const group = isGroup ? groupsMap[subject.subjectId] : null;
                const isAuthorRole = (subject as ISubjectPublicationClaim).publicationClaim === messages.manageSharedAndCreateNew;

                return (
                  <TableRow key={subject.subjectId} hover>
                    <TableCell align={cols[0].alignment} sx={isPublicationGranularAccess ? { width: '40%' } : undefined}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        {isUser ? <Avatar alt={user?.full_name} src={user?.avatar} /> : <Avatar><GroupsIcon /></Avatar>}
                        <Typography variant="h4">{isGroup ? group?.name : user?.full_name}</Typography>
                      </Stack>
                    </TableCell>
                    {isPublicationGranularAccess ? (
                      <TableCell align={cols[1].alignment} sx={{ width: '50%', padding: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                          <FormControl sx={{ minWidth: 180, width: 180 }}>
                            <Select<IPublicationPrivilege>
                              value={(subject as ISubjectPublicationClaim).publicationClaim}
                              onChange={(e) => {
                                onPublicationPrivilegeChange!(e, subject.subjectId);
                              }}
                              displayEmpty
                              inputProps={{ 'aria-label': 'Without label' }}
                              renderValue={(selected) => {
                                const selectedRole = rolesList.find((r) => r.value === selected);
                                return selectedRole?.label || '';
                              }}
                              MenuProps={MenuProps}
                            >
                              <MenuItem value={messages.manageSharedAndViewAll} sx={{ '&.Mui-selected': { backgroundColor: 'transparent', color: 'inherit' } }}>
                                <Box>
                                  <Typography variant="subtitle1">{messages.reviewer}</Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                                    {messages.reviewerDescription}
                                  </Typography>
                                </Box>
                              </MenuItem>

                              <MenuItem value={messages.manageShared} sx={{ '&.Mui-selected': { backgroundColor: 'transparent', color: 'inherit' } }}>
                                <Box>
                                  <Typography variant="subtitle1">{messages.contributor}</Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                                    {messages.contributorDescription}
                                  </Typography>
                                </Box>
                              </MenuItem>

                              <MenuItem value={messages.manageSharedAndCreateNew} sx={{ '&.Mui-selected': { backgroundColor: 'transparent', color: 'inherit' } }}>
                                <Box>
                                  <Typography variant="subtitle1">{messages.author}</Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                                    {messages.authorDescription}
                                  </Typography>
                                </Box>
                              </MenuItem>

                              <MenuItem value={messages.manageAll} sx={{ '&.Mui-selected': { backgroundColor: 'transparent', color: 'inherit' } }}>
                                <Box>
                                  <Typography variant="subtitle1">{messages.editor}</Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                                    {messages.editorDescription}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            </Select>
                          </FormControl>
                          {(subject as ISubjectPublicationClaim).publicationClaim === messages.manageSharedAndCreateNew && isAuthorRole && (
                            <FormControl sx={{ minWidth: 180, width: 180 }}>
                              <Select<string[]>
                                multiple
                                value={(subject as ISubjectPublicationClaim).sectionIds || []}
                                onChange={(e: SelectChangeEvent<string[]>) => onSectionIdsChange!(e, subject.subjectId)}
                                input={<OutlinedInput placeholder="Select sections" sx={{ width: 180 }} />}
                                renderValue={(selected) => {
                                  const selectedArray = Array.isArray(selected) ? selected : [];
                                  const selectedNames = selectedArray
                                    .map(id => findSectionById(folderStructure || [], id)?.displayName)
                                    .filter(Boolean)
                                    .join(', ');
                                  return selectedNames || 'Select sections';
                                }}
                                MenuProps={MenuProps}
                              >
                                {renderOptions(folderStructure || [], (subject as ISubjectPublicationClaim).sectionIds || [])}
                              </Select>
                            </FormControl>
                          )}
                        </Stack>
                      </TableCell>
                    ) : (
                      !isBulk ? (
                        (() => {
                          const publicationClaim = subjectPublicationClaimsList?.find(
                            (claim: ISubjectPublicationClaim) => claim.subjectId === subject.subjectId
                          )?.publicationClaim;

                          if (publicationClaim) {
                            if (publicationClaim === messages.manageAll) {
                              // For manage_all, show all checkboxes checked and disabled
                              return [STORIES_VIEWER_PRIVILEGE, STORIES_EDITOR_PRIVILEGE, STORIES_PDF_GENERATION_PRIVILEGE, STORIES_PUBLISHER_PRIVILEGE].map((priv) => (
                                <TableCell key={priv} align={cols[1].alignment}>
                                  <Checkbox name={priv} checked disabled />
                                </TableCell>
                              ));
                            } else if (publicationClaim === messages.manageSharedAndViewAll) {
                              // For manage_shared_and_view_all
                              return [STORIES_VIEWER_PRIVILEGE, STORIES_EDITOR_PRIVILEGE, STORIES_PDF_GENERATION_PRIVILEGE, STORIES_PUBLISHER_PRIVILEGE].map((priv) => (
                                <TableCell key={priv} align={cols[1].alignment}>
                                  <Checkbox
                                    name={priv}
                                    checked={
                                      priv === STORIES_VIEWER_PRIVILEGE || // Always checked for view
                                      ('storyClaims' in subject && subject.storyClaims.includes(priv as IStoryPrivilege))
                                    }
                                    onChange={(e) => onPrivilegeChange!(e, subject.subjectId)}
                                    disabled={priv === STORIES_VIEWER_PRIVILEGE}
                                  />
                                </TableCell>
                              ));
                            }
                          }
                          // Default behavior for other cases
                          return [STORIES_VIEWER_PRIVILEGE, STORIES_EDITOR_PRIVILEGE, STORIES_PDF_GENERATION_PRIVILEGE, STORIES_PUBLISHER_PRIVILEGE].map((priv) => (
                            <TableCell key={priv} align={cols[1].alignment}>
                              <Checkbox
                                name={priv}
                                checked={'storyClaims' in subject && subject.storyClaims.includes(priv as IStoryPrivilege)}
                                onChange={(e) => onPrivilegeChange!(e, subject.subjectId)}
                                disabled={priv === STORIES_VIEWER_PRIVILEGE}
                              />
                            </TableCell>
                          ));
                        })()
                      ) : (
                        // Simple checkbox rendering for bulk mode
                        [STORIES_VIEWER_PRIVILEGE, STORIES_EDITOR_PRIVILEGE, STORIES_PDF_GENERATION_PRIVILEGE, STORIES_PUBLISHER_PRIVILEGE].map((priv) => (
                          <TableCell key={priv} align={cols[1].alignment}>
                            <Checkbox
                              name={priv}
                              checked={'storyClaims' in subject && subject.storyClaims.includes(priv as IStoryPrivilege)}
                              onChange={(e) => onPrivilegeChange!(e, subject.subjectId)}
                              disabled={priv === STORIES_VIEWER_PRIVILEGE}
                            />
                          </TableCell>
                        ))
                      )
                    )}
                    <TableCell align={cols[2].alignment}>
                      {(isBulk && isPublicationGranularAccess) || 
                       !subjectPublicationClaimsList?.some((claim: ISubjectPublicationClaim) => claim.subjectId === subject.subjectId) ? (
                        <SvgIcon
                          component={CloseIcon}
                          inheritViewBox
                          fontSize="small"
                          sx={styles.closeIcon}
                          onClick={() => onRemoveClick(subject.subjectId)}
                        />
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </MuiTable>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        {isPublicationGranularAccess && (
          <Stack direction="row" alignItems="center">
            <EmailIcon />
            <Typography variant="body2" sx={styles.mlHalf}>
              {messages.emailInvites}
            </Typography>
          </Stack>
        )}
        <Button variant="text" color="secondary" onClick={onClose}>
          {messages.cancel}
        </Button>
        <Button variant="contained" onClick={onSave}>
          {messages.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
